import { useCallback, useState, useEffect, useMemo } from "react";
import { ColumnFiltersState } from "@tanstack/react-table";
import { getTenantIdFromToken } from "@/utils/auth";
import { useCachedAccessToken } from "@/hooks/useCachedAccessToken";
import {
  LegislationTableFilterContext,
  LegislationTableFilterContextValue,
} from "./hooks/useLegislationTableFilterState";
import useLegislationTableFilterStore from "./stores/legislation-table-filter.store";
interface LegislationTableFilterProviderProps {
  children: React.ReactNode;
}

export const LegislationTableFilterProvider = ({
  children,
}: LegislationTableFilterProviderProps) => {
  const getCachedToken = useCachedAccessToken();
  const markInitialized = useLegislationTableFilterStore(
    (state) => state.markInitialized
  );
  const initializedTenants = useLegislationTableFilterStore(
    (state) => state.initializedTenants
  );
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const id = await getTenantIdFromToken(getCachedToken);
        setTenantId(id || null);
      } catch {
        setTenantId(null);
      }
    };
    fetchTenantId();
  }, [getCachedToken]);

  const initializeDefaults = useCallback(
    (
      columnFilters: ColumnFiltersState,
      defaultsPlan: {
        sourceKey: string;
        childFilters: { id: string; value: unknown }[];
      } | null
    ): ColumnFiltersState => {
      if (!defaultsPlan) return columnFilters;

      // Check if sessionStorage has data - if it does, session has already started
      // and we should not apply defaults (even if filters are empty)
      if (typeof window !== "undefined") {
        const sessionData = sessionStorage.getItem("legislation-table-state");
        if (sessionData) {
          // Session has data, don't apply defaults
          return columnFilters;
        }
      }

      // Check if user already has selections - if they do, don't apply defaults
      const hasDatasource = columnFilters.some((f) => f.id === "datasource");
      if (hasDatasource) {
        // User has made selections, don't apply defaults
        return columnFilters;
      }

      // Only apply defaults if sessionStorage is empty (first time in this session)
      const baseFilters = columnFilters.filter((f) => f.id !== "datasource");
      const filtered = baseFilters.filter(
        (f) => !defaultsPlan.childFilters.some((c) => c.id === f.id)
      );

      return [
        { id: "datasource", value: defaultsPlan.sourceKey },
        ...filtered,
        ...defaultsPlan.childFilters,
      ];
    },
    []
  );

  const markDefaultsInitialized = useCallback(() => {
    if (tenantId) {
      markInitialized(tenantId);
    }
  }, [tenantId, markInitialized]);

  // Make hasInitializedDefaults reactive by subscribing to store changes
  const hasInitializedDefaults = useMemo(() => {
    return tenantId ? initializedTenants.includes(tenantId) : false;
  }, [tenantId, initializedTenants]);

  const value: LegislationTableFilterContextValue = {
    hasInitializedDefaults,
    initializeDefaults,
    markDefaultsInitialized,
  };

  return (
    <LegislationTableFilterContext.Provider value={value}>
      {children}
    </LegislationTableFilterContext.Provider>
  );
};
